/**
 * Navigation précédent/suivant entre éléments d'une liste (devis, chantiers, sous-traitants…).
 * Stocke la liste courante en sessionStorage sous la clé fournie, permet de naviguer
 * avec des boutons ou raccourcis clavier ← / →.
 */
import { useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export function usePrevNextNav({ storageKey, currentId, routePrefix }) {
  const navigate = useNavigate();

  const list = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  }, [storageKey]);

  const idx = list.indexOf(currentId);
  const prev = idx > 0 ? list[idx - 1] : null;
  const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;

  const goPrev = useCallback(() => { if (prev) navigate(`${routePrefix}/${prev}`); }, [prev, navigate, routePrefix]);
  const goNext = useCallback(() => { if (next) navigate(`${routePrefix}/${next}`); }, [next, navigate, routePrefix]);

  useEffect(() => {
    const handler = (e) => {
      // Skip when the user is typing
      const t = e.target?.tagName;
      if (t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT' || e.target?.isContentEditable) return;
      if (e.key === 'ArrowLeft' && prev) { e.preventDefault(); goPrev(); }
      if (e.key === 'ArrowRight' && next) { e.preventDefault(); goNext(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next, goPrev, goNext]);

  return { prev, next, goPrev, goNext, index: idx, total: list.length };
}

/** À appeler depuis la page LISTE pour persister l'ordre courant. */
export function persistListOrder(storageKey, ids) {
  try { sessionStorage.setItem(storageKey, JSON.stringify(ids)); } catch (_) {}
}
